import type { ButtonHTMLAttributes, ElementType, HTMLAttributes } from "react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils";

// ─── Public types ────────────────────────────────────────────────────────────

export type StatCardColor =
  | "blue"
  | "amber"
  | "green"
  | "purple"
  | "orange"
  | "red";

// ─── Style tables ────────────────────────────────────────────────────────────

const valueColorStyles: Record<StatCardColor, string> = {
  blue: "text-blue-600",
  amber: "text-amber-600",
  green: "text-green-600",
  purple: "text-purple-600",
  orange: "text-orange-600",
  red: "text-red-600",
};

const shellStyles =
  "px-3 py-2.5 rounded-lg bg-white border border-gray-200 text-gray-900";

const activeStyles = "bg-blue-50 shadow-sm";

const interactiveStyles = [
  "cursor-pointer",
  "text-left",
  "transition-[border-color,background-color,box-shadow,transform] duration-150",
  "motion-safe:active:scale-[0.99]",
  "hover:border-gray-300 hover:bg-gray-50",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navigator-primary",
];

// ─── StatCardBase (internal) ─────────────────────────────────────────────────

interface StatCardBaseProps {
  as: ElementType;
  label: string;
  value: string | number;
  color?: StatCardColor;
  className?: string;
  loading?: boolean;
}

function StatCardBase({
  as: Wrapper,
  label,
  value,
  color,
  className,
  loading,
  children: _children,
  ...rest
}: StatCardBaseProps & Record<string, unknown>) {
  return (
    <Wrapper className={cn(shellStyles, className)} {...rest}>
      <Typography variant="small" color="muted" truncate className="block min-w-0">
        {label}
      </Typography>
      <div aria-live="polite" aria-busy={loading ? true : undefined}>
        <Typography
          variant="headline3"
          tabularNums
          truncate
          color={color ? valueColorStyles[color] : undefined}
        >
          {loading ? (
            <span
              aria-hidden="true"
              className="inline-block h-5 w-12 align-middle rounded bg-gray-200 motion-safe:animate-pulse"
            />
          ) : (
            value
          )}
        </Typography>
      </div>
    </Wrapper>
  );
}

// ─── StatCard (display) ──────────────────────────────────────────────────────

export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  color?: StatCardColor;
  loading?: boolean;
}

export function StatCard(props: StatCardProps) {
  return <StatCardBase as="div" {...props} />;
}

// ─── StatCardButton (interactive) ────────────────────────────────────────────

export interface StatCardButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  value: string | number;
  color?: StatCardColor;
  active?: boolean;
  loading?: boolean;
}

export function StatCardButton({
  active,
  color,
  loading,
  className,
  ...rest
}: StatCardButtonProps) {
  return (
    <StatCardBase
      as="button"
      type="button"
      color={color}
      loading={loading}
      aria-pressed={active}
      className={cn(interactiveStyles, active && activeStyles, className)}
      {...rest}
    />
  );
}
